// ABOUTME: Icon-only action for the EditNode header bar: a tooltip-labelled IconButton in the bar's contrast colour.
// ABOUTME: Wraps the button in a span so the tooltip still works while the button is disabled or loading.

import { Box, Button, CircularProgress, IconButton, MenuItem, Tooltip } from '@ds/coolshop';
import {
    cloneElement,
    forwardRef,
    isValidElement,
    MouseEvent,
    ReactElement,
    ReactNode,
    useContext,
} from 'react';
import { createPortal } from 'react-dom';

import { HeaderBarMenuContext } from './HeaderBarMenuContext';

/** The props a DS icon element accepts for the menu-entry rendering. */
type MenuIconProps = { fontSize?: 'small'; color?: 'primary' };

export interface HeaderBarIconButtonProps {
    label: string;
    icon: ReactNode;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    /** Shows a spinner in place of the icon and disables the button. */
    loading?: boolean;
    /** 'button' gives the action more weight: a light pill with icon and label instead of the icon alone. */
    variant?: 'icon' | 'button';
    id?: string;
    'aria-controls'?: string;
    'aria-haspopup'?: boolean | 'true' | 'menu';
    'aria-expanded'?: boolean | 'true' | 'false';
}

export const HeaderBarIconButton = forwardRef<HTMLButtonElement, HeaderBarIconButtonProps>(
    (
        { label, icon, onClick, disabled = false, loading = false, variant = 'icon', ...rest },
        ref,
    ) => {
        const menu = useContext(HeaderBarMenuContext);

        // Inside a HeaderBarActionsMenu the action is a menu entry, projected into the open menu
        // while this component (and the dialogs it owns) stays mounted in the bar.
        if (menu) {
            if (!menu.container) return null;
            return createPortal(
                <MenuItem
                    // Same look as the sidebar's MenuActionIcon: small primary icon, nested in a
                    // Box because the DS MenuItem greys out a direct SvgIcon child.
                    icon={
                        <Box display="flex">
                            {isValidElement(icon)
                                ? cloneElement(icon as ReactElement<MenuIconProps>, {
                                      fontSize: 'small',
                                      color: 'primary',
                                  })
                                : icon}
                        </Box>
                    }
                    disabled={disabled || loading}
                    onClick={e => {
                        onClick(e as unknown as MouseEvent<HTMLButtonElement>);
                        menu.close();
                    }}
                >
                    {label}
                </MenuItem>,
                menu.container,
            );
        }

        if (variant === 'button') {
            return (
                <Button
                    ref={ref}
                    variant="contained"
                    invertedColor
                    contrastColor="primary"
                    startIcon={icon}
                    onClick={onClick}
                    disabled={disabled}
                    loading={loading}
                    {...rest}
                >
                    {label}
                </Button>
            );
        }

        return (
            <Tooltip title={label}>
                {/* A disabled element does not fire events, so wrap it in a span for the Tooltip to
                listen on. */}
                <span>
                    <IconButton
                        ref={ref}
                        aria-label={label}
                        onClick={onClick}
                        disabled={disabled || loading}
                        invertedColor
                        contrastColor="primary"
                        {...rest}
                    >
                        {loading ? (
                            // The bar is a primary AppBar and the button is disabled while it
                            // loads: without an explicit contrast colour the spinner takes the
                            // primary (or disabled grey) tint and disappears into the bar.
                            <Box display="flex" color="primary.contrastText">
                                <CircularProgress size="small" color="inherit" />
                            </Box>
                        ) : (
                            icon
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        );
    },
);
HeaderBarIconButton.displayName = 'HeaderBarIconButton';
